// Reparte el renglón Tarjeta entre varias tarjetas, cada una con su monto y su comprobante opcional.
import { Lock, Plus, Unlock, X, Zap } from 'lucide-react';
import { Button, Modal } from '@/kit';
import { cn } from '@/lib/cn';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { receiptErrors } from '../salePayments';
import {
    addCard,
    assignAllToCard,
    cardDraft,
    maxCards,
    removeCard,
    toggleCardLock,
    updateCard,
    type CardEntry,
    type FixedPaymentRows,
} from '../fixedPaymentRows';
import {
    ERROR_TEXT,
    IconButton,
    RECEIPT_INPUT,
    RowAmount,
    sanitize,
} from './FixedPaymentRowsParts';

export interface CardSplitsModalProps {
    rows: FixedPaymentRows;
    totalCents: number;
    onChange: (next: FixedPaymentRows) => void;
    onClose: () => void;
    readOnly?: boolean;
}

/** Una tarjeta en $0 sin otra libre con saldo no tiene de dónde tomar monto. */
function isCardAmountDisabled(rows: FixedPaymentRows, card: CardEntry): boolean {
    if (card.amountCents > 0) return false;
    return !rows.card.cards.some((c) => c.id !== card.id && !c.locked && c.amountCents > 0);
}

function receiptText(card: CardEntry): string {
    const parts = [
        card.authCode ? `Autorización ${card.authCode}` : '',
        card.cardLast4 ? `•••• ${card.cardLast4}` : '',
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : 'Sin comprobante';
}

export function CardSplitsModal({
    rows,
    totalCents,
    onChange,
    onClose,
    readOnly = false,
}: CardSplitsModalProps) {
    const cards = rows.card.cards;
    const limit = maxCards(rows);
    const canAdd = !readOnly && cards.length < limit;

    return (
        <Modal
            isOpen
            onClose={onClose}
            size="lg"
            title={readOnly ? 'Pago con tarjeta' : 'Reparte el monto entre tarjetas'}
            description={
                readOnly
                    ? undefined
                    : `Hasta ${String(limit)} ${limit === 1 ? 'tarjeta' : 'tarjetas'}. Con una sola, efectivo y transferencia absorben la diferencia; con varias, la absorben las otras tarjetas sin candado.`
            }
        >
            <div className="flex flex-col gap-2">
                {cards.map((card, idx) => {
                    const number = String(idx + 1);
                    const errors = receiptErrors(cardDraft(card));
                    return (
                        <div
                            key={card.id}
                            role="group"
                            aria-label={`Tarjeta ${number}`}
                            className="flex flex-col gap-1.5 rounded-[10px] border border-border bg-card p-2.5"
                        >
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="w-[72px] flex-none text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                                    Tarjeta {number}
                                </span>
                                {readOnly ? (
                                    <span className="min-w-0 flex-1 font-mono text-[12.5px]">
                                        {receiptText(card)}
                                    </span>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            autoComplete="off"
                                            maxLength={64}
                                            placeholder="Autorización (opcional)"
                                            value={card.authCode}
                                            aria-label={`Autorización de la tarjeta ${number}`}
                                            aria-invalid={errors.authCode ? true : undefined}
                                            onChange={(e) => {
                                                onChange(
                                                    updateCard(
                                                        rows,
                                                        card.id,
                                                        { authCode: e.target.value.slice(0, 64) },
                                                        totalCents,
                                                    ),
                                                );
                                            }}
                                            className={cn(
                                                RECEIPT_INPUT,
                                                'min-w-0 flex-1 font-mono',
                                            )}
                                        />
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="off"
                                            maxLength={4}
                                            placeholder="Últimos 4"
                                            value={card.cardLast4}
                                            aria-label={`Últimos 4 dígitos de la tarjeta ${number}`}
                                            aria-invalid={errors.cardLast4 ? true : undefined}
                                            onChange={(e) => {
                                                onChange(
                                                    updateCard(
                                                        rows,
                                                        card.id,
                                                        {
                                                            cardLast4: sanitize(
                                                                e.target.value,
                                                                /\D/g,
                                                                4,
                                                            ),
                                                        },
                                                        totalCents,
                                                    ),
                                                );
                                            }}
                                            className={cn(
                                                RECEIPT_INPUT,
                                                'w-[88px] text-center font-mono',
                                            )}
                                        />
                                    </>
                                )}
                                <RowAmount
                                    cents={card.amountCents}
                                    label={`Monto de la tarjeta ${number}`}
                                    readOnly={readOnly}
                                    disabled={isCardAmountDisabled(rows, card)}
                                    onCommit={(cents) => {
                                        onChange(
                                            updateCard(
                                                rows,
                                                card.id,
                                                { amountCents: cents },
                                                totalCents,
                                            ),
                                        );
                                    }}
                                />
                                {!readOnly && cards.length > 1 && (
                                    <>
                                        <IconButton
                                            label={
                                                card.locked
                                                    ? `Desbloquear monto de la tarjeta ${number}`
                                                    : `Bloquear monto de la tarjeta ${number}`
                                            }
                                            pressed={card.locked}
                                            onClick={() => {
                                                onChange(toggleCardLock(rows, card.id));
                                            }}
                                        >
                                            {card.locked ? (
                                                <Lock className="size-4" aria-hidden />
                                            ) : (
                                                <Unlock className="size-4" aria-hidden />
                                            )}
                                        </IconButton>
                                        <IconButton
                                            label={`Asignar todo a la tarjeta ${number}`}
                                            onClick={() => {
                                                onChange(assignAllToCard(rows, card.id));
                                            }}
                                        >
                                            <Zap className="size-4" aria-hidden />
                                        </IconButton>
                                        <button
                                            type="button"
                                            aria-label={`Quitar tarjeta ${number}`}
                                            onClick={() => {
                                                onChange(removeCard(rows, card.id, totalCents));
                                            }}
                                            className="flex size-9 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                                        >
                                            <X className="size-4" aria-hidden />
                                        </button>
                                    </>
                                )}
                            </div>
                            {!readOnly &&
                                [errors.authCode, errors.cardLast4]
                                    .filter((msg): msg is string => !!msg)
                                    .map((msg) => (
                                        <p key={msg} className={ERROR_TEXT}>
                                            {msg}
                                        </p>
                                    ))}
                        </div>
                    );
                })}
                {!readOnly && (
                    <div className="flex flex-col gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={!canAdd}
                            leftIcon={<Plus aria-hidden />}
                            onClick={() => {
                                onChange(addCard(rows, crypto.randomUUID()));
                            }}
                            className="self-start border-dashed"
                        >
                            Agregar tarjeta
                        </Button>
                        {!canAdd && (
                            <span className="text-[11px] leading-[1.35] text-muted-foreground">
                                El cobro ya tiene 3 formas de pago.
                            </span>
                        )}
                    </div>
                )}
            </div>

            <div className="mt-4 flex items-center justify-between gap-3 border-t border-border pt-3">
                <span className="text-[12.5px] tabular-nums text-muted-foreground">
                    Total en tarjetas{' '}
                    <span className="font-semibold text-foreground">
                        {formatMoneyCents(rows.card.amountCents)}
                    </span>
                </span>
                <Button size="sm" onClick={onClose}>
                    Listo
                </Button>
            </div>
        </Modal>
    );
}
